import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useViewOnly } from '../contexts/ViewOnlyContext';
import { 
  Baby, 
  LogOut, 
  Calendar, 
  User, 
  Activity, 
  Syringe, 
  Ruler, 
  Thermometer,
  ShieldAlert,
  FileText,
  Stethoscope,
  Heart
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

function DependentDashboard() {
  const [dependent, setDependent] = useState(null);
  const [babyRecords, setBabyRecords] = useState([]);
  const [selectedBaby, setSelectedBaby] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { enableViewOnlyMode } = useViewOnly();

  // Helper function to normalize gender display
  const normalizeGender = (gender) => {
    if (!gender) return 'Unknown';
    const lowerGender = gender.toLowerCase();
    if (lowerGender === 'boy' || lowerGender === 'male' || lowerGender === 'm') return 'Male';
    if (lowerGender === 'girl' || lowerGender === 'female' || lowerGender === 'f') return 'Female';
    return gender;
  };

  useEffect(() => {
    // Get dependent from localStorage
    const storedDependent = localStorage.getItem('dependent');
    const storedAccesses = localStorage.getItem('dependentAccesses');

    if (!storedDependent) {
      navigate('/dependent-login');
      return;
    }

    const dependentData = JSON.parse(storedDependent);
    const accessesData = JSON.parse(storedAccesses || '[]');

    setDependent(dependentData);

    // Fetch baby data for each access
    fetchBabyRecords(accessesData);
  }, [navigate]);

  const fetchBabyRecords = async (accesses) => {
    setLoading(true);
    const records = [];

    for (const access of accesses) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/access/view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ access_code: access.access_code }),
        });

        if (response.ok) {
          const data = await response.json();
          records.push(data);
        }
      } catch (error) {
        console.error('Error fetching baby record:', error);
      }
    }

    setBabyRecords(records);
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('dependent');
    localStorage.removeItem('dependentAccesses');
    navigate('/dependent-login');
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    const ageInMs = today - birthDate;
    const ageInDays = Math.floor(ageInMs / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(ageInDays / 365);
    const months = Math.floor((ageInDays % 365) / 30);
    const days = (ageInDays % 365) % 30;
    
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? 's' : ''} ${days} day${days > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-pink-50">
        <div className="text-center">
          <Baby className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading baby records...</p>
        </div>
      </div>
    );
  }

  if (selectedBaby) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-pink-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedBaby(null)}
                >
                  ← Back
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    {selectedBaby.baby.name}'s Health Record
                  </h1>
                  <p className="text-gray-600">
                    {calculateAge(selectedBaby.baby.date_of_birth)} • {normalizeGender(selectedBaby.baby.gender)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Viewing as: {dependent.name}</span>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Vaccinations</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedBaby.vaccinations?.filter(v => v.status === 'given').length || 0}
                    </p>
                  </div>
                  <Syringe className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Latest Weight</p>
                    <p className="text-2xl font-bold text-green-600">
                      {(() => {
                        const weight = selectedBaby.growth_records?.sort((a, b) => 
                          new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
                        )[0]?.weight || selectedBaby.baby.weight;
                        return weight > 0 ? `${weight} kg` : 'Not recorded';
                      })()}
                    </p>
                  </div>
                  <Ruler className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Temperature</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {selectedBaby.temperature_readings?.sort((a, b) => 
                        new Date(b.created_at) - new Date(a.created_at)
                      )[0]?.temperature || '--'} {selectedBaby.temperature_readings?.[0]?.unit || '°F'}
                    </p>
                  </div>
                  <Thermometer className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Information Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Medical Conditions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Medical Conditions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedBaby.medical_conditions?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedBaby.medical_conditions.map((condition, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded">
                        <p className="font-semibold">{condition.condition_name}</p>
                        <p className="text-sm text-gray-600">{condition.description}</p>
                        <p className="text-xs text-gray-500 mt-1">Status: {condition.status}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No medical conditions recorded</p>
                )}
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5" />
                  Allergies
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedBaby.allergies?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedBaby.allergies.map((allergy, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded border border-red-200">
                        <p className="font-semibold text-red-700">{allergy.allergen}</p>
                        <p className="text-sm text-gray-600">{allergy.reaction}</p>
                        <p className="text-xs text-gray-500 mt-1">Severity: {allergy.severity}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No allergies recorded</p>
                )}
              </CardContent>
            </Card>

            {/* Birth Record */}
            {selectedBaby.birth_record && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Baby className="w-5 h-5" />
                    Birth Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Birth Weight:</span>
                      <span className="font-semibold">{selectedBaby.birth_record.birth_weight} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Length:</span>
                      <span className="font-semibold">{selectedBaby.birth_record.length} cm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Head Circumference:</span>
                      <span className="font-semibold">{selectedBaby.birth_record.head_circumference} cm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Delivery Method:</span>
                      <span className="font-semibold">{selectedBaby.birth_record.delivery_method}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Medical Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="w-5 h-5" />
                  Recent Medical Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedBaby.medical_sessions?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedBaby.medical_sessions.slice(0, 3).map((session, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded">
                        <p className="font-semibold">{session.doctor_name}</p>
                        <p className="text-sm text-gray-600">{session.diagnosis}</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date(session.session_date).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No medical sessions recorded</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-pink-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Welcome, {dependent?.name}!
              </h1>
              <p className="text-gray-600">View baby health records you have access to</p>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Baby Records */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {babyRecords.length > 0 ? (
            babyRecords.map((record, idx) => (
              <Card 
                key={idx} 
                className={`cursor-pointer hover:shadow-lg transition-shadow overflow-hidden ${
                  ['male', 'boy', 'm'].includes(record.baby.gender?.toLowerCase()) ? 'male-balloons' : 'female-balloons'
                }`}
                onClick={() => {
                  // Enable view-only mode and redirect to main dashboard
                  enableViewOnlyMode(dependent.name);
                  // Store baby data temporarily for main dashboard
                  sessionStorage.setItem('viewOnlyBaby', JSON.stringify(record.baby));
                  navigate('/');
                  window.location.reload(); // Reload to load baby data
                }}
              >
                <CardHeader>
                  <div className="flex items-center justify-center mb-4">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                      ['female', 'girl', 'f'].includes(record.baby.gender?.toLowerCase()) ? 'bg-pink-100' : 'bg-blue-100'
                    }`}>
                      <Baby className={`w-10 h-10 ${
                        ['female', 'girl', 'f'].includes(record.baby.gender?.toLowerCase()) ? 'text-pink-600' : 'text-blue-600'
                      }`} />
                    </div>
                  </div>
                  <CardTitle className="text-center">{record.baby.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{calculateAge(record.baby.date_of_birth)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{normalizeGender(record.baby.gender)}</span>
                    </div>
                    {record.baby.blood_type && (
                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        <span>Blood Type: {record.baby.blood_type}</span>
                      </div>
                    )}
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <Baby className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No baby records available</p>
              <p className="text-sm text-gray-500 mt-2">
                You haven't been granted access to any baby records yet
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DependentDashboard;
